import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
    initiateNotionIntegration,
    getNotionIntegrationStatus,
    toggleNotionAutoSync,
    triggerNotionManualSync,
    disconnectNotionIntegration,
    type NotionIntegrationStatus,
    type NotionIntegrationInitiateResponse,
    type NotionSyncToggleResponse,
    type NotionDisconnectResponse
} from '@/services/api';

export interface UseNotionIntegrationReturn {
    // State
    status: NotionIntegrationStatus | null;
    isLoading: boolean;
    isConnecting: boolean;
    isDisconnecting: boolean;
    isSyncing: boolean;

    // Actions
    connect: () => Promise<void>;
    toggleAutoSync: () => Promise<void>;
    manualSync: () => Promise<void>;
    disconnect: () => Promise<void>;
    refreshStatus: () => Promise<void>;

    // Utilities
    formatDate: (dateString: string | null) => string;
    isConnected: boolean;
}

export const useNotionIntegration = (): UseNotionIntegrationReturn => {
    const [status, setStatus] = useState<NotionIntegrationStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    // Load initial status
    const loadStatus = useCallback(async () => {
        try {
            setIsLoading(true);
            const integrationStatus = await getNotionIntegrationStatus();
            setStatus(integrationStatus);
        } catch (error) {
            console.error('Failed to load Notion integration status:', error);
            // Don't show error toast for initial load failures
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Refresh status
    const refreshStatus = useCallback(async () => {
        try {
            const integrationStatus = await getNotionIntegrationStatus();
            setStatus(integrationStatus);
        } catch (error) {
            console.error('Failed to refresh Notion integration status:', error);
            toast.error('Failed to refresh integration status');
        }
    }, []);

    // Connect to Notion
    const connect = useCallback(async () => {
        try {
            setIsConnecting(true);
            const redirectUri = `${window.location.origin}/notion/status`;
            const response: NotionIntegrationInitiateResponse = await initiateNotionIntegration(redirectUri);

            // Store state for security verification
            localStorage.setItem('notion_auth_state', response.authorization_url.split('state=')[1]);

            // Redirect to Notion authorization
            window.location.href = response.authorization_url;
        } catch (error) {
            console.error('Failed to initiate Notion integration:', error);
            toast.error('Failed to connect to Notion. Please try again.');
        } finally {
            setIsConnecting(false);
        }
    }, []);

    // Toggle auto-sync
    const toggleAutoSync = useCallback(async () => {
        if (!status) return;

        try {
            const newAutoSyncEnabled = !status.auto_sync_enabled;
            const response: NotionSyncToggleResponse = await toggleNotionAutoSync(newAutoSyncEnabled);

            toast.success(response.message);

            // Update local status
            setStatus(prev => prev ? {
                ...prev,
                auto_sync_enabled: newAutoSyncEnabled
            } : null);
        } catch (error) {
            console.error('Failed to toggle auto-sync:', error);
            toast.error('Failed to update sync settings.');
        }
    }, [status]);

    // Manual sync
    const manualSync = useCallback(async () => {
        try {
            setIsSyncing(true);
            await triggerNotionManualSync();

            toast.success('Sync started successfully! Please check back in a few minutes.');

        } catch (error) {
            console.error('Failed to start manual sync:', error);
            toast.error('Failed to start sync.');
        } finally {
            setIsSyncing(false);
        }
    }, []);

    // Disconnect integration
    const disconnect = useCallback(async () => {
        if (!status?.integration_id) return;

        try {
            setIsDisconnecting(true);
            const response: NotionDisconnectResponse = await disconnectNotionIntegration();

            toast.success(response.message);

            // Clear status
            setStatus(null);
        } catch (error) {
            console.error('Failed to disconnect Notion integration:', error);
            toast.error('Failed to disconnect integration.');
        } finally {
            setIsDisconnecting(false);
        }
    }, [status]);

    // Format date utility
    const formatDate = useCallback((dateString: string | null): string => {
        if (!dateString) return 'Never';
    
        try {
            const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
            const date = new Date(utcString);
            const now = new Date();
    
            // Always use UTC timestamps for difference
            const diffInMs = now.getTime() - date.getTime();
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    
            if (diffInHours < 1) return 'Just now';
            if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
            // Show local date for older timestamps
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    }, []);
    

    // Computed properties
    const isConnected = status?.is_connected ?? false;

    // Load status on mount
    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    return {
        // State
        status,
        isLoading,
        isConnecting,
        isDisconnecting,
        isSyncing,

        // Actions
        connect,
        toggleAutoSync,
        manualSync,
        disconnect,
        refreshStatus,

        // Utilities
        formatDate,
        isConnected
    };
};
